import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const CloudMigration = () => {
  const phrases = [
    "Data Center Transformation",
    "Cloud Migration"
  ];

  const cardData = [
    { type: 'purple-clay', content: '☁️' },
    { type: 'smooth-wave', content: '📦' },
    { type: 'abstract-blocks', content: '🔄' },
    { type: 'pink-sphere', content: '⚡' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "🔍", title: "Discovery & Application Mapping", desc: "Scan legacy setups automatically to isolate workload dependencies, compute baselines, and clear pathways." },
    { icon: "🏗️", title: "Migration Strategy Design", desc: "Formulate custom Rehosting (Lift-and-Shift), Replatforming, or Refactoring blueprints scaled for your operational limits." },
    { icon: "🗄️", title: "Zero-Loss Database Replications", desc: "Execute live multi-tier schema conversions and ongoing data synchronization passes with near-zero transactional disruptions." },
    { icon: "🌐", title: "Secure Software-Defined Networks", desc: "Construct hybrid configurations, transit gateways, network bridges, and identity boundaries to safely isolate cloud traffic." },
    { icon: "🔄", title: "Automated Workload Transfers", desc: "Utilize elite cloud transport engines to safely replicate block storage disks and runtime setups into targeted zones." },
    { icon: "🛡️", title: "Zero-Trust Landing Zone Setup", desc: "Enforce strict organizational access control, structural logging pathways, and continuous protection guards upfront." },
    { icon: "🏁", title: "Cutover & Performance Validation", desc: "Coordinate systematic DNS routing transitions, data integrity tests, and post-migration computing reviews." }
  ];

  const advancedServices = [
    { icon: "⚡", title: "Monolith Architecture Modernization", desc: "Deconstruct aging system software into responsive serverless structures and dynamic microservices." },
    { icon: "📉", title: "Immediate FinOps Efficiency Tiers", desc: "Configure resource limitations, auto-scaling hooks, and enterprise cloud budgets to slice runtime cost waste." },
    { icon: "📦", title: "Workload Containerization Traces", desc: "Repackage target applications into portable, standardized Docker images for smooth deployment on Kubernetes." },
    { icon: "📡", title: "Comprehensive System Telemetry", desc: "Deploy persistent monitoring metrics and tracking agents to secure broad operational visibility across nodes." },
    { icon: "🧬", title: "Automated Failure Recovery", desc: "Structure multi-region disaster response plans, hot cloud reserves, and instant backup syncing patterns." },
    { icon: "🔧", title: "Infrastructure-as-Code Templating", desc: "Bake target computing spaces entirely into clean, reusable Terraform code files to simplify cluster builds." },
    { icon: "⚖️", title: "Regulatory Cloud Compliance Audits", desc: "Validate target hosting nodes against complex security configurations to ensure native compliance layers." },
    { icon: "🚀", title: "Hybrid Edge-Network Routing", desc: "Set up globally balanced web content caching parameters to minimize delay times for target users." },
    { icon: "🧪", title: "Post-Migration Reliability Testing", desc: "Run simulated traffic spikes and failure injections to ensure shifted clusters maintain continuous runtimes." }
  ];

  const technologies = {
    "Migration Engines": ["AWS Application Migration Service", "Azure Migrate Core", "GCP Migration Center", "Carbonite / VMware HCX"],
    "Target Cloud Hubs": ["Amazon Web Services", "Microsoft Azure Cloud", "Google Cloud Platform", "Hybrid OpenStack Layers"],
    "Database Conversion": ["AWS DMS (Data Migration)", "Azure Database Migration Service", "pgloader / Ora2Pg Scripts", "Striim Real-Time Sync"],
    "IaC & Control Layers": ["Terraform Automation", "Ansible Systems", "Docker / Kubernetes Assemblies", "Packer Base Image Builders"],
    "Monitoring & Costing": ["Prometheus & Grafana Systems", "AWS CloudWatch / Azure Monitor", "Datadog Telemetry Platforms", "FinOps Kubecost Scanners"]
  };

  const processSteps = [
    { num: "01", title: "Discovery Scans", desc: "Inventorying hardware configurations, mapping system integrations, and tracking performance points." },
    { num: "02", title: "Strategy Architecture", desc: "Grouping applications into clear treatment cycles and deciding optimal destination environments." },
    { num: "03", title: "Landing Zone Framework", desc: "Building core security structures, identity boundaries, network topologies, and log management channels." },
    { num: "04", title: "Pilot Execution Data", desc: "Moving non-critical workloads first to prove network speed, script rules, and target scaling behavior." },
    { num: "05", title: "Mass Workload Shift", desc: "Running synchronized block-level replication steps alongside active database mirror tracks." },
    { num: "06", title: "System Switchover", desc: "Redirecting active users to fresh cloud targets with verified zero data losses during window updates." },
    { num: "07", title: "FinOps Optimization", desc: "Right-sizing computing nodes, adjusting auto-scale zones, and eliminating orphaned volume storage costs." }
  ];

  const industries = [
    "Enterprise Banking Networks", "High-Scale Digital SaaS Platforms", "Healthcare Systems & EMR", "Global E-Commerce Portals",
    "Logistics & Transit Systems", "Media Streaming Ecosystems", "Insurance & Claim Processing", "Automotive & Telematics Hubs",
    "Manufacturing & Supply Webs", "Energy Grid Computing Nodes", "Government & Public Cloud Spheres", "EdTech Learning Operations"
  ];

  const designBenefits = [
    "Eliminated On-Premises CapEx", "Elastic Sub-Second Workload Scale", "Airtight Zero-Trust Boundaries",
    "Drastically Lower Runtime Costs", "Standardized Declarative Coding", "Near-Zero Recovery Objectives",
    "Total Structural System Visibility", "Complete Vendor Agility Options"
  ];

  const whyChooseUs = [
    "Certified Cloud Migration Architects", "Proven Zero-Downtime Data Methods", "Declarative, Versioned Infrastructure",
    "Deep Dependency Discovery Prowess", "Security-First Target Node Hardening", "Optimized Workload Density Layouts",
    "Comprehensive Post-Migration Support", "Transparent Cost Optimization Models", "Enterprise-Grade SLA Assurances",
    "Experienced Legacy Database Refactorers", "Native Cloud Integration Paradigms", "100% Application Continuity Commitments"
  ];

  return (
    <>

      <Helmet>
        {/* Primary SEO */}
        <title>
          Cloud Migration Services | Cloud Consulting & Migration Solutions | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="Cloud Migration Services | Cloud Consulting & Migration Solutions | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. provides secure and scalable cloud migration services, including cloud consulting, application migration, data migration, infrastructure migration, AWS, Microsoft Azure, Google Cloud, DevOps, and cloud modernization solutions."
        />

        <meta
          name="keywords"
          content="Cloud Migration, Cloud Migration Services, Cloud Consulting, Cloud Computing, AWS Migration, Microsoft Azure Migration, Google Cloud Migration, Application Migration, Data Migration, Infrastructure Migration, Cloud Modernization, Cloud Infrastructure, Hybrid Cloud Solutions, Multi Cloud Services, DevOps Services, Cloud Security, Cloud Transformation, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/cloud-migration"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Professional Cloud Migration Services | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Accelerate your digital transformation with cloud migration services from Affsoft IT Solutions Pvt. Ltd. We specialize in AWS, Azure, Google Cloud, application migration, infrastructure modernization, and secure cloud solutions."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/cloud-migration"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Cloud Migration Services | Cloud Consulting & Migration Solutions | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Move your applications and infrastructure to the cloud with confidence. Affsoft IT Solutions Pvt. Ltd. delivers AWS, Azure, Google Cloud, and end-to-end cloud migration services."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="cloud-migration-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#07101a' // dark cloud transition fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.12 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Slow-Scrolling Marquee Text Background Wrapper */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>

          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>Data Center Transformation &nbsp; and &nbsp;</span>
              <span>Cloud Migration </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-info fs-3">✳</div>
                <h1 className="fw-bold mb-3 display-5">Cloud Migration</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At Affsoft IT Solutions Pvt. Ltd., we guide intricate physical server footprints into modern, automated cloud systems. Our expert migration tracks combine precise structural scanning tools, secure data pipelines, and zero-downtime execution metrics to unlock elite computing scale safely.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>About Affsoft</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor text-info fs-4 animate-blink">|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= CORE MIGRATION SOLUTIONS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-primary px-3 py-2 rounded-pill mb-2 text-uppercase">Modern Infrastructure</span>
              <h2 className="fw-bold text-dark display-6">Seamless Data Transitions Programmed for Continuity</h2>
              <p className="text-muted mt-3">
                Unplanned workloads shifts trigger operational blindspots, data losses, and unexpected overspending. At Affsoft IT Solutions Pvt. Ltd., we implement robust framework strategies that maintain service availability and fully safeguard data records.
              </p>
            </div>

            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3 text-primary">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Cloud Optimization Benefits Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Why Shift Workloads to the Cloud?</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED MODERNIZATION OPTIONS ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-info text-dark px-3 py-2 rounded-pill mb-2 text-uppercase">Optimization Layer</span>
              <h2 className="fw-bold text-dark display-6">Cloud Optimization Backed by Tactical Architecture</h2>
              <p className="text-muted mt-3">
                We extend normal migration pathways into complete cloud maturity models. From breaking down data bottlenecks to building continuous container structures, our processes deliver high-efficiency clusters that keep costs minimal.
              </p>
            </div>

            <div className="row g-4">
              {advancedServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3 text-info">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-info text-dark px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold">Cloud Migration Stack</span>
              <h2 className="fw-bold display-6">Systems & Enterprise Architecture We Deploy</h2>
              <p className="text-light-50">We control complex compute relocations using trusted, industry-standard tool paths.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold text-info mb-3 border-bottom border-secondary pb-2">{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="text-info opacity-75">•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Migration Roadmap</span>
              <h2 className="fw-bold text-dark display-6">Our Engineered Cloud Lifecycle</h2>
              <p className="text-muted">A comprehensive milestones track converting raw hardware instances safely into modular cloud infrastructure.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-info border-4 h-100 shadow-sm">
                    <div className="fw-bold text-info display-6 mb-2">{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Industries We Empower</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🏢 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE AFFSOFT ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with Affsoft IT Solutions Pvt. Ltd.?</h2>
              <p className="text-muted">We join reliable automated testing with expert cloud design to move corporate applications with total safety.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="text-info fs-4">🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>

    </>
  );
};

export default CloudMigration;